import { createBrowserHistory } from "history";
import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { HomeCategories } from "@Components/molecules/home/HomeCategories";
import { HomeCollections } from "@Components/molecules/home/HomeCollections";
import { HomeHero } from "@Components/molecules/home/HomeHero";
import { HomeService } from "@Components/molecules/home/HomeService";
import { IconsInfos } from "@Components/molecules/home/IconsInfos";
import { SeoDescription } from "@Components/molecules/home/SeoDescription";
import { SeoProducts } from "@Components/molecules/home/SeoProducts";
import { SeoTitle } from "@Components/molecules/home/SeoTitle";
import { HEADER_HEIGHT } from "@Components/organisms/Header/index";
import { useGlobalLoader } from "@Components/WithGlobalLoader";
import WithLocale from "@Components/WithLocale";
import useBannerKountriesHeight from "@Hooks/banner/useBannerKountriesHeight";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { ApiV2ErrorResponse } from "@Types/api/error";
import { ComponentsMapping, Page, Section, Tile } from "@Types/pagify";
import { SettingsContent } from "@Types/settings";
import UserContent from "@Types/user";
import { apiV2ErrorManager } from "@Utils/error";
import { getCsrfToken, setMeta } from "@Utils/meta";
import { convertPageFromApiToContent } from "@Utils/pagify";
import { convertSettingsFromApiToContent } from "@Utils/settings";
import { setDataLayerMandatoryPageTrackers } from "@Utils/track";
import "./style.scss";

const history = createBrowserHistory();

interface Props {
  home: Page;
}

interface ContainerProps {
  userData: UserContent;
}

export const Home: FunctionComponent<Props> = ({ home }) => {
  const intl = useIntl();

  useEffect(() => {
    document.title = intl.formatMessage({ id: "cheerz_Home_meta_title" });
    setMeta("description", intl.formatMessage({ id: "cheerz_Home_meta_description" }));
  }, []);

  const homeComponentsList: ComponentsMapping = {
    categories_web: HomeCategories,
    icons_web: IconsInfos,
    occasions_web: HomeCollections,
    seo_description_web: SeoDescription,
    seo_products_web: SeoProducts,
    seo_title_web: SeoTitle,
    service_web: HomeService,
  };

  const largeHeroImageSection = home.sections.filter(
    (section) => section.displayType === "large_hero_image_web"
  )[0];
  const largeHeroImageTiles = home.tiles.filter((tile) =>
    new Set(largeHeroImageSection.tileSlugs).has(tile.slug)
  );
  const smallHeroImageSection = home.sections.filter(
    (section) => section.displayType === "small_hero_image_web"
  )[0];
  const smallHeroImageTiles = home.tiles.filter((tile) =>
    new Set(smallHeroImageSection.tileSlugs).has(tile.slug)
  );

  const HomeComponents = home.sections.map((section: Section) => {
    const Component = homeComponentsList[section.displayType];
    const tiles = section.tileSlugs.reduce((results, slug) => {
      const tile = home.tiles.find((tile) => tile.slug === slug);
      if (tile) {
        return results.concat([tile]);
      } else return results;
    }, [] as Tile[]);

    return Component ? (
      <Component key={section.displayType} section={section} tiles={tiles} />
    ) : null;
  });

  const { isLargeScreen } = useBreakpoint();
  const bannerKountriesHeight = useBannerKountriesHeight();
  const marginTop = isLargeScreen ? HEADER_HEIGHT + bannerKountriesHeight : 0;

  return (
    <div className="Home" style={{ marginTop }}>
      <HomeHero
        largeHeroImageTiles={largeHeroImageTiles}
        smallHeroImageTiles={smallHeroImageTiles}
      />
      {HomeComponents}
    </div>
  );
};

const HomeContainer: FunctionComponent<ContainerProps> = ({ userData }) => {
  const [home, setHome] = useState<Page>();
  const [settings, setSettings] = useState<SettingsContent>();
  const { setLoading } = useGlobalLoader();
  const intl = useIntl();
  const locale = intl.locale;

  const cheerzApiProvider = new CheerzApiProvider(locale, getCsrfToken(), true);

  useEffect(() => {
    cheerzApiProvider
      .getSettings(locale)
      .then((settingsData) => {
        setSettings(convertSettingsFromApiToContent(settingsData.response));
        return cheerzApiProvider.getPage("home", settingsData.response).then((data) => {
          setHome(convertPageFromApiToContent(data.response));
          setLoading(false);
        });
      })
      .catch((error: ApiV2ErrorResponse) => apiV2ErrorManager(error));
  }, []);

  useEffect(() => {
    if (home && settings) {
      setDataLayerMandatoryPageTrackers(
        settings?.currency,
        settings?.lang,
        "Homepage",
        "Homepage",
        window.location.pathname,
        userData
      );
    }
  }, [home, settings]);

  return <WithLocale history={history}>{home && <Home home={home} />}</WithLocale>;
};

export default HomeContainer;
